import React from "react";
import { Box, Typography, Grid, Button } from "@mui/material";
import theme from "../../../theme";
import { PrimaryButton, SecondaryButton } from "../../../SystemDesign/styles";
import { useTranslation } from 'react-i18next';

// Icons for advantages
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ExploreIcon from '@mui/icons-material/Explore';
import FlexibleIcon from '@mui/icons-material/AllInclusive';
import StarIcon from '@mui/icons-material/Star';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import VisibilityIcon from '@mui/icons-material/Visibility';

const AdvantageCard = ({ icon, title, description, audience }) => {
  return (
    <Box
      sx={{
        backgroundColor: audience === "client" ? theme.colors.primary[50] : theme.colors.secondary[50],
        borderRadius: theme.borderRadius.xl,
        padding: { xs: '24px', sm: '28px', md: '32px' },
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        boxShadow: theme.boxShadow.sm,
        border: `1px solid ${audience === "client" ? theme.colors.primary[100] : theme.colors.secondary[100]}`,
        '&:hover': {
          transform: 'translateY(-8px)',
          boxShadow: theme.boxShadow.lg,
          backgroundColor: audience === "client" ? theme.colors.primary[75] : theme.colors.secondary[75],
        },
      }}
    >
      <Box
        sx={{
          backgroundColor: audience === "client" ? theme.colors.primary[100] : theme.colors.secondary[100],
          borderRadius: '50%',
          width: { xs: 64, md: 72 },
          height: { xs: 64, md: 72 },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: { xs: '20px', md: '24px' },
          transition: 'all 0.3s ease',
        }}
      >
        {React.cloneElement(icon, { 
          sx: { 
            fontSize: { xs: 32, md: 36 }, 
            color: audience === "client" ? theme.colors.primary[600] : theme.colors.secondary[600] 
          } 
        })}
      </Box>
      <Typography
        variant="h5"
        sx={{
          fontWeight: 700,
          fontSize: { xs: '1.125rem', sm: '1.25rem', md: '1.375rem' },
          color: audience === "client" ? theme.colors.primary[700] : theme.colors.secondary[700],
          marginBottom: { xs: '12px', md: '16px' },
          lineHeight: 1.3,
        }}
      >
        {title}
      </Typography>
      <Typography
        variant="body1"
        sx={{
          color: theme.colors.text.secondary,
          fontSize: { xs: '0.875rem', sm: '0.9375rem', md: '1rem' },
          lineHeight: 1.6,
          flexGrow: 1,
          maxWidth: '280px',
          textAlign: 'justify',
        }}
      >
        {description}
      </Typography>
    </Box>
  );
};

const AdvantagesSection = () => {
  const { t } = useTranslation();
  const brandName = t('brand.name');
  const handleScrollToSection = (sectionId) => {
    const el = document.querySelector(`#${sectionId}`)
    if (el && typeof el.scrollIntoView === "function") {
      el.scrollIntoView({ behavior: "smooth", block: "start" })
    }
  }

  const clientAdvantages = [
    {
      icon: <AccessTimeIcon />,
      title: t('landing_advantages.client.time.title'),
      description: t('landing_advantages.client.time.description'),
    },
    {
      icon: <ExploreIcon />,
      title: t('landing_advantages.client.discovery.title'),
      description: t('landing_advantages.client.discovery.description'),
    },
    {
      icon: <FlexibleIcon />,
      title: t('landing_advantages.client.flexibility.title'),
      description: t('landing_advantages.client.flexibility.description'),
    },
  ];

  const salonAdvantages = [
    {
      icon: <VisibilityIcon />,
      title: t('landing_advantages.pro.visibility.title'),
      description: t('landing_advantages.pro.visibility.description'),
    },
    {
      icon: <TrendingUpIcon />,
      title: t('landing_advantages.pro.management.title'),
      description: t('landing_advantages.pro.management.description'),
    },
    {
      icon: <StarIcon />,
      title: t('landing_advantages.pro.loyalty.title'),
      description: t('landing_advantages.pro.loyalty.description'),
    },
  ];

  return (
    <Box
      sx={{
        maxWidth: '1400px',
        margin: '0 auto',
        padding: { 
          xs: '48px 20px', 
          sm: '64px 32px', 
          md: '80px 40px',
          lg: '96px 48px'
        },
      }}
    >
      {/* Main heading */}
      <Box
        sx={{
          textAlign: 'center',
          marginBottom: { xs: 6, sm: 8, md: 10 },
        }}
      >
        <Typography
          variant="h2"
          sx={{
            fontSize: { xs: '2rem', sm: '2.5rem', md: '2.75rem', lg: '3.25rem' },
            fontWeight: 800,
            color: theme.colors.primary[800],
            marginBottom: { xs: 2, md: 3 },
            letterSpacing: '-0.02em',
            position: 'relative',
            '&:after': {
              content: '""',
              position: 'absolute',
              bottom: { xs: -12, md: -16 },
              left: '50%',
              transform: 'translateX(-50%)',
              width: { xs: 60, md: 80 },
              height: { xs: 3, md: 4 },
              backgroundColor: theme.colors.secondary[400],
              borderRadius: 2,
            },
          }}
        >
          {t('landing_advantages.title', { appName: brandName })}
        </Typography>
        <Typography
          variant="subtitle1"
          sx={{
            fontSize: { xs: '1rem', md: '1.125rem' },
            color: theme.colors.text.secondary,
            maxWidth: '600px',
            margin: '0 auto',
            lineHeight: 1.6,
            textAlign: 'justify',
          }}
        >
          {t('landing_advantages.subtitle', { appName: brandName })}
        </Typography>
      </Box>

      {/* Clients section */}
      <Box sx={{ marginBottom: { xs: 8, sm: 10, md: 12 } }}>
        <Typography
          variant="h3"
          sx={{
            fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
            fontWeight: 700,
            color: theme.colors.primary[600],
            textAlign: 'center',
            marginBottom: { xs: 4, sm: 5, md: 6 },
            letterSpacing: '-0.01em',
          }}
        >
          {t('landing_advantages.clients_title')}
        </Typography>

        <Box sx={{ maxWidth: '1200px', margin: '0 auto' }}>
          <Grid container spacing={{ xs: 3, sm: 4, md: 5 }} justifyContent="center">
            {clientAdvantages.map((advantage, index) => (
              <Grid 
                item 
                xs={12} 
                sm={6} 
                lg={4} 
                key={`client-${index}`}
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                }}
              >
                <Box sx={{ width: '100%', maxWidth: '360px' }}>
                  <AdvantageCard
                    icon={advantage.icon}
                    title={advantage.title}
                    description={advantage.description}
                    audience="client"
                  />
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>

        <Box 
          sx={{
            display: 'flex',
            justifyContent: 'center',
            marginTop: { xs: 4, sm: 5, md: 6 },
          }}
        >
          <Button
            onClick={() => handleScrollToSection('nosSalonSection')}
            sx={{
              ...SecondaryButton,
              backgroundColor: 'transparent',
              color: theme.colors.primary[600],
              borderColor: theme.colors.primary[400],
              borderWidth: '2px',
              borderStyle: 'solid',
              fontSize: { xs: '0.875rem', md: '0.9375rem' },
              fontWeight: 600,
              padding: { xs: '10px 24px', md: '12px 28px' },
              borderRadius: '24px',
              textTransform: 'none',
              transition: 'all 0.3s ease',
              '&:hover': {
                backgroundColor: theme.colors.primary[50],
                borderColor: theme.colors.primary[500],
                transform: 'translateY(-2px)',
              }
            }}
          >
            {t('landing_advantages.discover_salons')}
          </Button>
        </Box>
      </Box>

      {/* Salons section */}
      <Box>
        <Typography
          variant="h3"
          sx={{
            fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
            fontWeight: 700,
            color: theme.colors.secondary[600],
            textAlign: 'center',
            marginBottom: { xs: 4, sm: 5, md: 6 },
            letterSpacing: '-0.01em',
          }}
        >
          {t('landing_advantages.pro_title')}
        </Typography>

        <Box sx={{ maxWidth: '1200px', margin: '0 auto' }}>
          <Grid container spacing={{ xs: 3, sm: 4, md: 5 }} justifyContent="center">
            {salonAdvantages.map((advantage, index) => (
              <Grid 
                item 
                xs={12} 
                sm={6} 
                lg={4} 
                key={`salon-${index}`}
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                }}
              >
                <Box sx={{ width: '100%', maxWidth: '360px' }}>
                  <AdvantageCard
                    icon={advantage.icon}
                    title={advantage.title}
                    description={advantage.description}
                    audience="salon"
                  />
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>

      </Box>
    </Box>
  );
};

export default AdvantagesSection;